import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { LeaderboardTeam } from "../../../types/leaderboard";
import { buildIndividualLeaderboard } from "../../../utils/leaderboard/leaderboard-data";
import { PLAYER_ROLES, type PlayerRole } from "../../../utils/leaderboard/roles";
import { teamLogoUrl } from "../../../utils/teamLogo";

export type LeaderboardTab = "teams" | "players";

interface UseLeaderboardTabsOptions {
  teams: LeaderboardTeam[];
  onTabChange?: (tab: LeaderboardTab) => void;
}

export const useLeaderboardTabs = ({ teams, onTabChange }: UseLeaderboardTabsOptions) => {
  const { strings } = useLocalization();
  const { localized, goTo } = useLocalePath();
  const [tab, setTab] = useState<LeaderboardTab>("players");
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [playerTeamFilter, setPlayerTeamFilter] = useState<string>("all");
  const [playerRoleFilter, setPlayerRoleFilter] = useState<string>("all");

  useEffect(() => {
    onTabChange?.(tab);
  }, [tab, onTabChange]);

  const enrichedTeams = useMemo(
    () =>
      teams.map((team) => ({
        ...team,
        logoUrl: team.logoUrl || teamLogoUrl(team.teamId),
        fullName: team.fullName || team.teamName,
      })),
    [teams],
  );

  const players = useMemo(
    () =>
      buildIndividualLeaderboard(enrichedTeams, {
        teamId: playerTeamFilter === "all" ? null : playerTeamFilter,
        role: playerRoleFilter === "all" ? null : (playerRoleFilter as PlayerRole),
      }),
    [enrichedTeams, playerTeamFilter, playerRoleFilter],
  );

  const showTeamOnPlayer = playerTeamFilter === "all";
  const teamLogoAlt = (teamName: string) =>
    strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamName);
  const rankSiteLabel = (site: string) =>
    strings.LEADERBOARD.VIEW_PLAYER_RANK_SITE.replace("{site}", site);

  const toggleExpanded = (teamId: string) => {
    setExpandedTeamId((current) => (current === teamId ? null : teamId));
  };

  return {
    strings,
    localized,
    goTo,
    tab,
    setTab,
    expandedTeamId,
    toggleExpanded,
    playerTeamFilter,
    setPlayerTeamFilter,
    playerRoleFilter,
    setPlayerRoleFilter,
    enrichedTeams,
    players,
    showTeamOnPlayer,
    teamLogoAlt,
    rankSiteLabel,
    playerRoles: PLAYER_ROLES,
  };
};
